import React, { FC, useCallback, useEffect, useState } from 'react';
import { Formik, useField, useFormikContext } from 'formik';
import ReactSelect from 'react-select';
import dayjs from 'dayjs';

import usePrefix from 'utils/usePrefix';
import { ResponseError } from 'utils/api';
import { resqlExpertAdminApi } from 'utils/api/resqlexpert';
import {
  ResqlExpertSubmissionResponse,
  SimpleUser,
} from 'utils/api/resqlexpert.d';
import { notificationTypes } from 'utils/constants';
import { useApp } from 'App';
import { ActionTypes } from 'App/types';

import { StyledFormField } from 'pages/Schools/School/styles';
import { Label } from 'components/FormField/styles';
import Button from 'components/Button';
import ToggleSwitch from 'components/ToggleSwitch';
import NoData from 'components/NoData';
import Loader from 'components/Loader';
import { ExpertFilterSelectWrapperStyled } from 'components/Modals/ExpertFilterModal/styles';
import { customSelectStyles } from 'components/Select/styles';
import { CategoryKey, TABS } from 'components/ResqlExpert/constants';
import { FormActionsStyled } from 'components/ResqlExpert/SubmissionForm/styles';
import SubmissionFormContent from 'components/ResqlExpert/SubmissionForm/SubmissionFormContent';
import {
  SubmissionAssignedDataStyled,
  SubmissionDateStyled,
  SubmissionHeaderDetailStyled,
  SubmissionHeaderStyled,
  SubmissionNameStyled,
  SubmissionSchoolStyled,
  SubmissionStyled,
} from 'components/ResqlExpert/Submission/styles';

import Problem from '../SubmissionForm/Problem';
import Source from '../SubmissionForm/Source';
import Actions from '../SubmissionForm/Actions';
import Participants from '../SubmissionForm/Participants';
import Results from '../SubmissionForm/Results';
import Retrospective from '../SubmissionForm/Retrospective';
import { AdminFormValues, toFormValues, buildUpdateRequest } from './formMapping';

interface AdminOption {
  value: number;
  label: string;
}

const toOption = (admin: SimpleUser): AdminOption => ({
  value: admin.id,
  label: `${admin.first_name} ${admin.last_name}`,
});

interface AssigneeSelectProps {
  admins: SimpleUser[];
}

const AssigneeSelect: FC<AssigneeSelectProps> = ({ admins }) => {
  const t = usePrefix('ResqlExpert');
  const [field, , helpers] = useField<number | null>('assignee_id');
  const options = admins.map(toOption);
  const value = options.find((o) => o.value === field.value) ?? null;

  return (
    <ExpertFilterSelectWrapperStyled>
      <Label htmlFor="select_expert2">{t('select_expert')}</Label>
      <ReactSelect
        inputId="select_expert2"
        options={options}
        value={value}
        onChange={(opt) => helpers.setValue((opt as AdminOption | null)?.value ?? null)}
        isClearable
        placeholder={t('select_expert_placeholder')}
        styles={customSelectStyles}
      />
    </ExpertFilterSelectWrapperStyled>
  );
};

interface DetailBodyProps {
  detail: ResqlExpertSubmissionResponse;
}

const DetailBody: FC<DetailBodyProps> = ({ detail }) => {
  const t = usePrefix('ResqlExpert');
  const [activeTab, setActiveTab] = useState<CategoryKey>(TABS[0]);
  const { values, isSubmitting, dirty, submitForm } = useFormikContext<AdminFormValues>();

  return (
    <SubmissionStyled>
      <SubmissionHeaderStyled>
        <h2>{values.title}</h2>
        <SubmissionHeaderDetailStyled>
          <SubmissionNameStyled>
            {t('submitted_by_label', {
              name: `${detail.submitted_by.first_name} ${detail.submitted_by.last_name}`,
              email: detail.submitted_by.email,
            })}
            <SubmissionSchoolStyled>
              {t('school_label', { school: detail.school_name })}
            </SubmissionSchoolStyled>
          </SubmissionNameStyled>
          <SubmissionDateStyled>
            {dayjs(detail.created_at).format('DD-MM-YYYY')}
          </SubmissionDateStyled>
        </SubmissionHeaderDetailStyled>
      </SubmissionHeaderStyled>
      <SubmissionAssignedDataStyled>
        <AssigneeSelect admins={detail.available_admins ?? []} />
        <ExpertFilterSelectWrapperStyled>
          <StyledFormField label={t('submission_resolved')} margin={'0'}>
            <ToggleSwitch checked={values.is_completed} name="is_completed" />
          </StyledFormField>
        </ExpertFilterSelectWrapperStyled>
        <ExpertFilterSelectWrapperStyled>
          <StyledFormField label={t('submission_AI')} margin={'0'}>
            <ToggleSwitch checked={values.is_ready_for_training} name="is_ready_for_training" />
          </StyledFormField>
        </ExpertFilterSelectWrapperStyled>
      </SubmissionAssignedDataStyled>
      <SubmissionFormContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabMap={{
          PROBLEM: <Problem />,
          SOURCE: <Source />,
          ACTIONS: <Actions />,
          PARTICIPANTS: <Participants />,
          RESULTS: <Results />,
          RETROSPECTIVE: <Retrospective />,
        }}
      />
      <FormActionsStyled $isAdmin>
        <Button
          label={t('cta_save')}
          onClick={submitForm}
          color="primary"
          disabled={isSubmitting || !dirty}
        />
      </FormActionsStyled>
    </SubmissionStyled>
  );
};

interface Props {
  id?: number;
  fetchData?: () => Promise<unknown>;
}

const Submission: FC<Props> = ({ id, fetchData }) => {
  const t = usePrefix('ResqlExpert');
  const [, dispatch] = useApp();
  const [detail, setDetail] = useState<ResqlExpertSubmissionResponse | undefined>(undefined);

  const loadDetail = useCallback(async (submissionId: number) => {
    try {
      const response = await resqlExpertAdminApi.getSubmissionDetail(submissionId);
      setDetail(response);
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!id) {
      setDetail(undefined);
      return;
    }
    loadDetail(id);
  }, [id, loadDetail]);

  const onSubmit = useCallback(async (values: AdminFormValues) => {
    if (!id) return;
    try {
      const updated = await resqlExpertAdminApi.updateSubmission(id, buildUpdateRequest(values));
      setDetail(updated);
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { type: notificationTypes.success },
      });
      if (fetchData) await fetchData();
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  }, [id, dispatch, fetchData]);

  if (!id) return <NoData title={t('no_data')} />;
  if (!detail) return <Loader label={t('loading')} />;

  return (
    <Formik
      initialValues={toFormValues(detail)}
      enableReinitialize
      onSubmit={onSubmit}
    >
      <DetailBody detail={detail} />
    </Formik>
  );
};

export default Submission;
